import { useEffect, useState } from "react";
import { api, ApiError } from "@/lib/api";
import { oauthResumeResponse, publicClientResponse } from "@sitebrew/contracts/v1";
import type { z } from "zod";
import { BusyButton, ButtonLink } from "@/components/button";
import { LoginCard, LoginCardSubtitle, LoginCardTitle, Notice } from "@/components/ui/login-card";

/**
 * The one screen in the whole OAuth flow a person actually reads.
 *
 * Everything else — discovery, registration, the code exchange — happens
 * between two programs. This is where somebody is told *what they are about to
 * hand over*, and it is the only place they can say no. Two consequences the
 * rest of this file is written around:
 *
 *  - **the scopes are sentences, not identifiers.** `sites:write` means nothing
 *    to the person deciding; "create, rename and archive websites" is the same
 *    fact in the words they would use. A scope with no sentence is shown as
 *    itself rather than hidden, because hiding a permission from a consent
 *    screen is the one failure that cannot be recovered later;
 *  - **refusing is a button, not the browser's back arrow.** Denial is an
 *    answer the client is entitled to receive, and the server turns it into the
 *    `access_denied` the spec expects. Closing the tab instead leaves the
 *    client waiting for a redirect that never comes.
 */

/** What each scope this server issues means, in the words of the person deciding. */
const SCOPE_SENTENCES: Record<string, string> = {
  "sites:read": "See the websites in your workspace",
  "sites:write": "Create, rename and archive websites",
  "workspace:read": "See your workspace and who is in it",
};

/**
 * What `/oauth2/consent` answers with is `oauthResumeResponse` in
 * `@sitebrew/contracts/v1`, and `publicClientResponse` is what the server
 * says about a client that registered itself.
 *
 * **Read from the provider's implementation, not from its documentation** —
 * the two disagree, and trusting the documentation is what put a person on a
 * 404. The package's own OpenAPI block for this endpoint describes
 * `{ redirect_uri }`; `consentEndpoint` returns the authorize endpoint's answer,
 * which is `{ redirect: true, url }` (`@better-auth/oauth-provider@1.7.1`).
 * Reading the documented name yielded `undefined`, and the browser was sent to
 * the literal path `/undefined`.
 */
type PublicClient = z.infer<typeof publicClientResponse>;

/**
 * Where the access will actually be sent, taken out of this page's own query.
 *
 * **This is the load-bearing fact on the screen, and it is the only one that
 * cannot be faked** (DevGuru, 2026-08-25). Registration is open and a client
 * writes its own `name`, so it may register as "ChatGPT" and the name proves
 * nothing. `redirect_uri` is different: it is where the grant goes, so an
 * attacker cannot put somebody else's address there and still receive the
 * token. That is why it is shown first and the name second.
 *
 * **The scheme is part of the answer, and leaving it out was a defect** (same
 * review, on the first version of this). Custom schemes are accepted at
 * registration, and `chatgptconnector://chatgpt.com/cb` has the host
 * `chatgpt.com` — so a screen showing the host alone printed it byte for byte
 * identically to the real `https://chatgpt.com/cb`. The one thing the row is
 * for, telling those two apart, was exactly what it could not do.
 *
 * The path is still dropped: it is long, differs per client and adds nothing to
 * the decision, while length is what makes people skip the part that matters.
 * A host-less address (`mailto:`, `javascript:`) reads as unreadable rather
 * than as an empty string, and the screen says so out loud instead of quietly
 * dropping the row — an absent line next to a live *Allow* button is a decision
 * made on a fact nobody was shown.
 */
export function destinationHost(search: string): string | undefined {
  const value = new URLSearchParams(search).get("redirect_uri");
  if (!value) return undefined;
  try {
    const url = new URL(value);
    if (url.host === "") return undefined;
    return `${url.protocol}//${url.host}`;
  } catch {
    return undefined;
  }
}

export function ConsentScreen({ search, clientId, scopes }: {
  search: string;
  clientId: string | undefined;
  scopes: string[];
}) {
  const [pending, setPending] = useState<"accept" | "deny" | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [client, setClient] = useState<PublicClient | null>(null);

  const destination = destinationHost(search);

  /**
   * The client's self-declared name, if the server knows one.
   *
   * Fetched rather than required: a screen that cannot be shown until a second
   * call returns is a screen that fails closed on a decision the person is
   * entitled to make. A failure here leaves the name absent and changes
   * nothing else — the host and the scopes are what the decision rests on.
   */
  useEffect(() => {
    if (!clientId) return;
    let current = true;
    api(`/auth/oauth2/public-client?client_id=${encodeURIComponent(clientId)}`, publicClientResponse)
      .then((found) => {
        if (current) setClient(found);
      })
      .catch(() => {
        // Deliberately silent: an unnamed client is a screen with less on it,
        // not an error the person can act on.
      });
    return () => {
      current = false;
    };
  }, [clientId]);

  const answer = async (accept: boolean) => {
    setPending(accept ? "accept" : "deny");
    setFailure(null);
    try {
      const answered = await api("/auth/oauth2/consent", oauthResumeResponse, {
        method: "POST",
        body: { accept, oauth_query: search },
      });

      /**
       * No address means the flow cannot be finished, and **saying so is the
       * fix**.
       *
       * `window.location.assign(undefined)` does not throw: it navigates to the
       * string "undefined", so a missing field became a 404 page on our own
       * origin with no hint of what went wrong. A person who has just clicked
       * *Allow* deserves to know their answer was recorded and only the return
       * failed — and every future shape change to this endpoint now lands here
       * rather than in the address bar.
       */
      if (!answered?.url) {
        setPending(null);
        setFailure(
          "Your answer was recorded, but the application did not give us an address to return you to. Start the connection again from the application.",
        );
        return;
      }

      // A full navigation, not a router one: the destination belongs to the
      // client that started this and is not an address this app serves.
      window.location.assign(answered.url);
    } catch (err) {
      setPending(null);
      setFailure(
        err instanceof ApiError
          ? err.message
          : "The authorization could not be completed. Try connecting again.",
      );
    }
  };

  return (
    <LoginCard>
      <LoginCardTitle>Allow access to your websites?</LoginCardTitle>
      <LoginCardSubtitle>
        An application is asking to act in your Sitebrew workspace.
      </LoginCardSubtitle>

      {/*
        * Who is asking, in the order the facts can be trusted.
        *
        * The destination is first because it is the one thing an attacker
        * cannot forge — the access goes there and nowhere else. The name and
        * the address underneath it were written by whoever registered the
        * client, so they are shown *marked as such*: a name that looks
        * official is exactly what a forged one would look like.
        */}
      <dl className="my-4 space-y-2 text-sm">
        <div>
          <dt className="text-muted-foreground">Access will be sent to</dt>
          <dd className="font-medium">
            {destination ? (
              <code>{destination}</code>
            ) : (
              /*
               * Said rather than omitted. The row disappearing while *Allow*
               * stayed put would let somebody approve without ever being shown
               * the one fact the approval rests on.
               */
              <span className="text-destructive">
                an address we could not read — do not allow this unless you know what it is
              </span>
            )}
          </dd>
        </div>
        {client?.name ? (
          <div>
            <dt className="text-muted-foreground">It calls itself (not verified by us)</dt>
            <dd>
              {client.name}
              {client.uri ? (
                <>
                  {" — "}
                  <code>{client.uri}</code>
                </>
              ) : null}
            </dd>
          </div>
        ) : null}
        {clientId ? (
          <div>
            <dt className="text-muted-foreground">Registered as</dt>
            <dd>
              <code>{clientId}</code>
            </dd>
          </div>
        ) : null}
      </dl>

      {/*
        * The list is the promise. Anything granted that is not named here would
        * be access nobody agreed to, which is why the unknown-scope case shows
        * the raw value instead of skipping it.
        */}
      <ul className="my-4 space-y-2 text-sm">
        {scopes.length === 0 ? (
          <li className="text-muted-foreground">No permissions were requested.</li>
        ) : (
          scopes.map((scope) => (
            <li key={scope} className="flex gap-2">
              <span aria-hidden="true">•</span>
              <span>{SCOPE_SENTENCES[scope] ?? scope}</span>
            </li>
          ))
        )}
      </ul>

      {failure ? <Notice>{failure}</Notice> : null}

      <div className="flex gap-3">
        <BusyButton
          type="button"
          busy={pending === "accept"}
          disabled={pending !== null}
          onClick={() => void answer(true)}
        >
          Allow
        </BusyButton>
        <BusyButton
          type="button"
          variant="secondary"
          busy={pending === "deny"}
          disabled={pending !== null}
          onClick={() => void answer(false)}
        >
          Refuse
        </BusyButton>
      </div>

      {/*
        * This sentence has now been wrong in both directions, which is why it
        * carries its history. It first promised something the product could not
        * do ("sign out of the connected application, or remove it from your
        * workspace" — signing out of the console does not close `/mcp`). It was
        * then corrected to "there is no way to withdraw it here yet", which was
        * true on 2026-08-25 and became a lie the moment
        * `/settings/connected-applications` existed — so it is changed in the
        * same change that builds the screen, not afterwards.
        *
        * A consent screen is a commitment, not a caption. The link is a plain
        * `href` rather than a router `Link` on purpose: this screen is rendered
        * outside the authenticated tree and a person following it should arrive
        * through the front door, sign-in and all.
        */}
      <LoginCardSubtitle>
        Access lasts about an hour and the application can renew it, so it continues until it stops
        asking. You can disconnect it at any time in{" "}
        <a className="underline" href="/settings/connected-applications">
          Connected applications
        </a>
        .
      </LoginCardSubtitle>
      <ButtonLink href="/">Back to your websites</ButtonLink>
    </LoginCard>
  );
}
